import * as React from "react"
import type { Img, Module, Text, Tag } from "./types"

// Module markup for the entrances (see src/work/entrance.ts):
//   data-block  fill | image | outline | media
//   data-text   a headline or body copy inside a block
const TEXT = { "data-text": "" }
const block = (kind: "fill" | "image" | "outline" | "media") => ({ "data-block": kind })

const CDN = "https://framerusercontent.com/images/"
function srcSet(im: Img) {
    if (im.src) return (im.widths || [im.w]).map((w) => `${im.src}-${w}.webp ${w}w`).join(",")
    const q = `width=${im.w}&height=${im.h}`
    const steps = [512, 1024, 2048, 4096].filter((s) => s < Math.max(im.w, im.h))
    return [...steps.map((s) => `${CDN}${im.id}?scale-down-to=${s}&${q} ${Math.round((s / Math.max(im.w, im.h)) * im.w)}w`), `${CDN}${im.id}?${q} ${im.w}w`].join(",")
}

/** The first section's images load straight away, first (its entrance waits for them). */
const Eager = React.createContext(false)

/** The image layer inside a block (fills it, clipped to its corners). */
function Layer({ im, sizes, fit = "cover" }: { im: Img; sizes: string; fit?: "cover" | "contain" }) {
    const eager = React.useContext(Eager)
    return (
        <div className="w-layer">
            <img
                decoding="async"
                loading={eager ? "eager" : "lazy"}
                {...({ fetchpriority: eager ? "high" : undefined } as any)}
                width={im.w}
                height={im.h}
                sizes={sizes}
                srcSet={srcSet(im)}
                src={im.src ? `${im.src}-${Math.max(...(im.widths || [im.w]))}.webp` : `${CDN}${im.id}?width=${im.w}&height=${im.h}`}
                alt={im.alt || ""}
                style={{ objectFit: fit, objectPosition: im.pos || "center" }}
            />
        </div>
    )
}

/** An image block: the box carries the layout, the image fills it. */
function Picture({ im, className, sizes, fit, style }: { im: Img; className: string; sizes: string; fit?: "cover" | "contain"; style?: React.CSSProperties }) {
    return (
        <div className={className} style={style} {...block("image")}>
            <Layer im={im} sizes={sizes} fit={fit} />
        </div>
    )
}

/** The tag's contents: logo at the top, its lines at the bottom (one layout for every case). */
function TagIn({ tag }: { tag: Tag }) {
    const [x, y, w, h] = tag.logoBox || [20, 20, 53, 53]
    return (
        <>
            {tag.logo && (
                <img
                    className="w-tag-logo"
                    src={tag.logo}
                    alt=""
                    style={{
                        left: x,
                        top: y,
                        width: w,
                        // its designed size at most; in a narrower tag it scales down to
                        // fit, keeping the same margin on the right as on the left
                        maxWidth: `calc(100% - ${2 * x}px)`,
                        height: "auto",
                        aspectRatio: `${w} / ${h}`,
                        objectFit: "contain",
                    }}
                />
            )}
            <div className="w-tag-t" {...TEXT} style={{ color: tag.ink }}>
                <p>{tag.text}</p>
            </div>
        </>
    )
}

function Card({ text, className, style }: { text: Text; className: string; style?: React.CSSProperties }) {
    return (
        <div className={`w-card ${className}`} style={style} {...block("outline")}>
            <div className="w-card-in">
                <div className="w-title" {...TEXT}>
                    {text.phoneTitle ? (
                        <>
                            <p className="hide-m">{text.title}</p>
                            <p className="only-m">{text.phoneTitle}</p>
                        </>
                    ) : (
                        <p>{text.title}</p>
                    )}
                </div>
                <div className="w-body" {...TEXT}>
                    <p>{text.body}</p>
                </div>
            </div>
        </div>
    )
}

/** Plays while on screen (muted, looping, like Framer's Video). It starts
 *  loading two screens before (so it's ready to play on arrival), plays once on
 *  screen and pauses when off it. */
function Clip({ src, sound }: { src: string; sound?: boolean }) {
    const ref = React.useRef<HTMLVideoElement>(null)
    const [muted, setMuted] = React.useState(true)
    React.useEffect(() => {
        const v = ref.current
        if (!v) return
        const near = new IntersectionObserver(
            ([e]) => {
                if (!e.isIntersecting || v.preload === "auto") return
                v.preload = "auto"
                v.load()
                near.disconnect()
            },
            { rootMargin: "200% 0px" }
        )
        const on = new IntersectionObserver(
            ([e]) => {
                if (e.isIntersecting) {
                    if (v.preload !== "auto") ((v.preload = "auto"), v.load())
                    v.play().catch(() => {})
                } else v.pause()
            },
            { rootMargin: "20% 0px" }
        )
        near.observe(v)
        on.observe(v)
        return () => (near.disconnect(), on.disconnect())
    }, [])
    return (
        <div className="w-clip">
            {/\.(mp4|m4v|webm)$/.test(src) ? (
                <video ref={ref} src={src} loop muted playsInline preload="none" {...block("media")} />
            ) : (
                <video ref={ref} loop muted playsInline preload="none" disablePictureInPicture {...block("media")}>
                    <source src={`${src}-${PHONE ? "m" : "d"}.av1.mp4`} type='video/mp4; codecs="av01.0.08M.08"' />
                    <source src={`${src}-${PHONE ? "m" : "d"}.mp4`} type="video/mp4" />
                </video>
            )}
            {sound && (
                <button
                    className="w-sound"
                    type="button"
                    aria-label={muted ? "Turn the sound on" : "Turn the sound off"}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.stopPropagation()
                        const v = ref.current
                        if (!v) return
                        v.muted = !v.muted
                        setMuted(v.muted)
                        if (!v.muted) v.play().catch(() => {})
                    }}
                >
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="butt" strokeLinejoin="miter" aria-hidden="true">
                        <path d="M3 9h4l5-4v14l-5-4H3z" fill="currentColor" stroke="none" />
                        {muted ? <path d="M15.5 9l6 6M21.5 9l-6 6" /> : <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" />}
                    </svg>
                </button>
            )}
        </div>
    )
}

/** a clip's files for this screen (types.ts, Clip): its phone size on phones,
 *  AV1 first (the smallest), H.264 for the browsers that can't play AV1 */
const PHONE = typeof matchMedia !== "undefined" && matchMedia("(max-width: 1279.98px)").matches
const clipSrc = (c: { src: string }) => c.src

const D = "(min-width: 1280px)"
const sz = (desk: string, phone: string) => `${D} ${desk}, ${phone}`

function Section({ kind, first, children }: { kind: string; first?: boolean; children: React.ReactNode }) {
    return (
        <section className={`ws ws-${kind}`} id={first ? "cover" : undefined} data-nto-scene="">
            {children}
        </section>
    )
}

export function ModuleView({ m, first }: { m: Module; first: boolean }) {
    return (
        <Eager.Provider value={first}>
            <ModuleBody m={m} first={first} />
        </Eager.Provider>
    )
}

function ModuleBody({ m, first }: { m: Module; first: boolean }) {
    switch (m.type) {
        case "cover-sports":
            return (
                <Section kind="cs" first={first}>
                    <div className="cs-a w-fill w-tag" {...block("fill")}>
                        {m.tag && <TagIn tag={m.tag} />}
                    </div>
                    <div className="cs-b">
                        <Picture im={m.image} className="cs-img" sizes={sz("calc((100vw - 120px) * .3)", "calc(100vw - 60px)")} />
                        <Card text={m.text} className="cs-card" />
                    </div>
                    <div className="cs-c w-fill hide-m" {...block("fill")} />
                    <div className="cs-d hide-m">
                        <Picture im={m.side} className="cs-side" sizes="calc((100vw - 120px) * .16)" />
                    </div>
                </Section>
            )
        case "cover-automotive":
            return (
                <Section kind="ca" first={first}>
                    <div className="ca-tag w-tag" style={{ backgroundColor: m.tag.color }} {...block("fill")}>
                        <TagIn tag={m.tag} />
                    </div>
                    <div className="ca-main">
                        <div className="ca-row1">
                            <Picture im={m.hero} className="ca-hero" sizes={sz("calc((100vw - 120px) * .57)", "calc(100vw - 60px)")} />
                            <Card text={m.text} className="ca-card" />
                        </div>
                        <div className="ca-row2">
                            <Picture im={m.square} className="ca-sq hide-m" sizes="calc((100vw - 120px) * .29)" />
                            <div className="ca-wide hide-m">
                                <Picture im={m.wide} className="ca-wide-img" sizes="calc((100vw - 120px) * .43)" />
                            </div>
                        </div>
                    </div>
                </Section>
            )
        case "split":
            return (
                <Section kind="sp">
                    <Picture im={m.image} className="sp-main" sizes={sz("calc(100vw - 120px)", "330px")} />
                    <Picture im={m.phone} className="sp-phone only-m" sizes="calc(100vw - 60px)" />
                </Section>
            )
        case "trio":
            return (
                <Section kind="tr">
                    <div className="tr-l">
                        <Picture im={m.left} className="tr-l-img" sizes={sz("calc((100vw - 120px) * .26)", "calc(100vw - 60px)")} />
                    </div>
                    <div className="tr-fill w-fill" {...block("fill")} />
                    <div className="tr-r hide-m">
                        <Picture im={m.right} className="tr-r-img" sizes="calc((100vw - 120px) * .26)" />
                    </div>
                </Section>
            )
        case "framed":
            return (
                <Section kind="fr">
                    {m.top && <Picture im={m.top} className="fr-top only-m" sizes="calc(100vw - 60px)" />}
                    {m.frame && (
                        <div className="fr-frame only-m" {...block("fill")}>
                            <Picture im={m.frame} className="fr-frame-img" sizes="330px" fit="contain" />
                        </div>
                    )}
                    <Picture im={m.image} className="fr-img" sizes={sz("calc(100vw - 120px)", "calc(100vw - 60px)")} />
                </Section>
            )
        case "panel":
            return (
                <Section kind="pn">
                    <div className="pn-frame" {...block("fill")}>
                        {m.clip && m.poster ? (
                            <div className="pn-clip only-m" {...block("image")}>
                                <Layer im={m.poster} sizes="calc(100vw - 60px)" />
                                <Clip src={clipSrc(m.clip)} sound={m.clip.sound} />
                            </div>
                        ) : (
                            <div className="pn-fill w-fill only-m" {...block("fill")} />
                        )}
                        <Picture im={m.image} className="pn-img" sizes={sz("calc(100vw - 120px)", "calc(100vw - 60px)")} fit="contain" />
                    </div>
                </Section>
            )
        case "grid": {
            const total = m.columns.reduce((a, c) => a + c.w, 0)
            const one = m.columns.length === 1 && m.columns[0].cells.length === 1
            // phones: one column in reading order (top to bottom, then left to
            // right, as designed), every image keeping its designed proportions
            const at = m.columns.flatMap((c, i) => {
                let y = 0
                return c.cells.map((x, j) => ((y += x.h), { i, j, top: y - x.h }))
            })
            // phones: the images share the height left by the tag and the card by
            // their designed proportions, together filling all of it (the shares
            // add up to 1: flexbox gives shares that add up to less only that part)
            const ratio = (c: (typeof m.columns)[number], x: (typeof m.columns)[number]["cells"][number]) => x.h / (x.w || c.w)
            const shared = m.columns.reduce((a, c) => a + c.cells.reduce((b, x) => b + (x.image && x.phone !== false && !x.keep && !x.fit ? ratio(c, x) : 0), 0), 0) || 1
            const order = new Map(
                [...at].sort((a, b) => a.top - b.top || a.i - b.i).map((p, k) => [`${p.i}.${p.j}`, k])
            )
            return (
                <Section kind="gr">
                    <div
                        className={"gr" + (one ? " gr-one" : "") + (m.phoneCenter ? " gr-center" : "") + (m.phoneMiddle ? " gr-middle" : "") + (m.phoneHalves ? " gr-halves" : "")}
                        style={
                            m.phoneCenter
                                ? (() => {
                                      // (phones: together as high as the screen allows, each keeping its proportions)
                                      const shown = m.columns.flatMap((c) => c.cells.filter((x) => x.phone !== false && (x.image || x.desktop === false)).map((x) => (typeof x.keep === "number" ? 1 / x.keep : x.h / (x.w || c.w))))
                                      return { ["--n" as any]: shown.length, ["--sum" as any]: shown.reduce((a, b) => a + b, 0).toFixed(4) }
                                  })()
                                : undefined
                        }
                    >
                        {m.columns.map((c, i) => (
                            <div key={i} className={"gr-col" + (c.cells.some((x) => x.fit) ? " gr-col-fit" : "") + (c.cells.some((x) => x.clip?.box) ? " gr-col-box" : "")} style={{ flexGrow: c.w, ["--ar" as any]: `${c.w} / ${c.cells[0].h}` }}>
                                {c.cells.map((x, j) => {
                                    const keeps = x.keep || (m.phoneCenter && !x.card && !x.tag)
                                    const cls =
                                        "gr-cell" +
                                        (x.phone === false ? " hide-m" : "") +
                                        (x.desktop === false ? " hide-d" : "") +
                                        (x.w ? " gr-narrow" : "") +
                                        (x.end ? " gr-end" : "") +
                                        (x.fit ? " gr-fit" : "") +
                                        (keeps ? " gr-keep" : "") +
                                        (x.grow ? " gr-grow" : "") +
                                        (x.half ? " gr-half" : "") +
                                        (x.cls ? " " + x.cls : "") +
                                        (x.clip?.box ? " gr-boxfit" : "")
                                    const style = {
                                        ...(x.fit && x.image ? { aspectRatio: `${x.image.w} / ${x.image.h}` } : {}),
                                        ...(x.clip?.box ? { ["--bar" as any]: ((x.w || c.w) / x.h).toFixed(4) } : {}),
                                        // phones: its own proportions (gr-keep)
                                        ["--keep" as any]: typeof x.keep === "number" ? x.keep.toFixed(4) : ((x.w || c.w) / x.h).toFixed(4),
                                        ...(x.last ? { ["--last" as any]: 1 } : {}),
                                        flexGrow: x.h,
                                        ["--po" as any]: order.get(`${i}.${j}`),
                                        ["--pg" as any]: (ratio(c, x) / (x.image ? shared : 1)).toFixed(4),
                                        ...(x.w ? { width: `${((x.w / c.w) * 100).toFixed(3)}%` } : {}),
                                    }
                                    const share = ((x.w || c.w) / total).toFixed(3)
                                    if (x.image && x.clip?.box) {
                                        // a video inset on a plain panel, placed by its design position
                                        const cw = x.w || c.w, [bx, by, bw, bh, br = 12] = x.clip.box
                                        return (
                                            <div key={j} className={cls + " gr-boxed"} style={{ ...style, background: x.clip.bg }} {...block("image")}>
                                                <div
                                                    className="w-boxed"
                                                    style={{
                                                        left: `${((bx / cw) * 100).toFixed(3)}%`,
                                                        top: `${((by / x.h) * 100).toFixed(3)}%`,
                                                        width: `${((bw / cw) * 100).toFixed(3)}%`,
                                                        height: `${((bh / x.h) * 100).toFixed(3)}%`,
                                                        ["--br" as any]: `${((br / cw) * 100).toFixed(3)}cqw`,
                                                    }}
                                                >
                                                    <Layer im={x.image} sizes={sz(`calc((100vw - 120px) * ${share})`, "calc(100vw - 60px)")} />
                                                    <Clip src={clipSrc(x.clip)} sound={x.clip.sound} />
                                                </div>
                                                {x.label && (
                                                    <div className="gr-label" {...TEXT} style={{ top: `${((x.label.y / x.h) * 100).toFixed(3)}%`, color: x.label.color }}>
                                                        <p>{x.label.text}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    }
                                    if (x.image && x.clip)
                                        return (
                                            <div key={j} className={cls} style={style} {...block("image")}>
                                                <Layer im={x.image} sizes={sz(`calc((100vw - 120px) * ${share})`, "calc(100vw - 60px)")} />
                                                <Clip src={clipSrc(x.clip)} sound={x.clip.sound} />
                                            </div>
                                        )
                                    if (x.image)
                                        return (
                                            <Picture
                                                key={j}
                                                im={x.image}
                                                className={cls}
                                                style={style}
                                                sizes={sz(`calc((100vw - 120px) * ${share})`, "calc(100vw - 60px)")}
                                            />
                                        )
                                    if (x.card) return <Card key={j} text={x.card} className={cls + " gr-card"} style={style} />
                                    if (x.tag)
                                        return (
                                            <div key={j} className={cls + " gr-tag w-tag w-fill"} style={{ ...style, backgroundColor: x.tag.color }} {...block("fill")}>
                                                <TagIn tag={x.tag} />
                                            </div>
                                        )
                                    return <div key={j} className={cls + " gr-empty"} style={style} />
                                })}
                            </div>
                        ))}
                    </div>
                </Section>
            )
        }
        case "bricks": {
            // desktop: a CSS grid of the designed columns and rows (cells side by
            // side keep one top and height; a row with a card is at least as high
            // as the card needs); phones: as the grid, one column in reading order
            const widthOf = (c: (typeof m.cells)[number]) => m.cols.slice(c.at[0], c.at[0] + (c.at[2] || 1)).reduce((a, b) => a + b, 0)
            const heightOf = (c: (typeof m.cells)[number]) => m.rows.slice(c.at[1], c.at[1] + (c.at[3] || 1)).reduce((a, b) => a + b, 0)
            const total = m.cols.reduce((a, b) => a + b, 0)
            const shared =
                m.cells.reduce((a, c) => a + (c.image && c.phone !== false ? heightOf(c) / widthOf(c) : 0), 0) || 1
            const order = [...m.cells].map((c, k) => ({ c, k })).sort((a, b) => a.c.at[1] - b.c.at[1] || a.c.at[0] - b.c.at[0])
            const cardRows = new Set(m.cells.filter((c) => c.card).map((c) => c.at[1]))
            return (
                <Section kind="gr">
                    <div
                        className="gr gr-bricks"
                        style={{
                            gridTemplateColumns: m.cols.map((w) => `minmax(0, ${w}fr)`).join(" "),
                            gridTemplateRows: m.rows.map((h, r) => (cardRows.has(r) ? `minmax(min-content, ${h}fr)` : `minmax(0, ${h}fr)`)).join(" "),
                        }}
                    >
                        {order.map(({ c: x, k }, n) => {
                            const cls = "gr-cell" + (x.phone === false ? " hide-m" : "") + (x.cls ? " " + x.cls : "")
                            const style: React.CSSProperties = {
                                gridColumn: `${x.at[0] + 1} / span ${x.at[2] || 1}`,
                                gridRow: `${x.at[1] + 1} / span ${x.at[3] || 1}`,
                                ["--po" as any]: n,
                                ["--pg" as any]: ((heightOf(x) / widthOf(x)) / (x.image ? shared : 1)).toFixed(4),
                                ...(x.tag ? { alignSelf: "start", aspectRatio: `${widthOf(x)} / ${x.h}` } : {}),
                            }
                            const share = (widthOf(x) / total).toFixed(3)
                            if (x.image)
                                return (
                                    <div key={k} className={cls} style={style} {...block("image")}>
                                        <Layer im={x.image} sizes={sz(`calc((100vw - 120px) * ${share})`, "calc(100vw - 60px)")} />
                                        {x.clip && <Clip src={clipSrc(x.clip)} sound={x.clip.sound} />}
                                    </div>
                                )
                            if (x.card) return <Card key={k} text={x.card} className={cls + " gr-card"} style={style} />
                            if (x.tag)
                                return (
                                    <div key={k} className={cls + " gr-tag w-tag w-fill"} style={{ ...style, backgroundColor: x.tag.color }} {...block("fill")}>
                                        <TagIn tag={x.tag} />
                                    </div>
                                )
                            return <div key={k} className={cls + " gr-empty"} style={style} />
                        })}
                    </div>
                </Section>
            )
        }
        case "stack": {
            // phones: single images brought together (phone.ts), one under the
            // other, as large as the screen allows, each keeping its proportions
            const sum = m.items.reduce((a, it) => a + it.h / it.w, 0)
            return (
                <Section kind="st">
                    <div className="st" style={{ ["--n" as any]: m.items.length, ["--sum" as any]: sum.toFixed(4) }}>
                        {m.items.map((it, k) => (
                            <div key={k} className="st-cell" style={{ aspectRatio: `${it.w} / ${it.h}` }} {...block("image")}>
                                <Layer im={it.image} sizes="calc(100vw - 60px)" />
                                {it.clip && <Clip src={clipSrc(it.clip)} sound={it.clip.sound} />}
                            </div>
                        ))}
                    </div>
                </Section>
            )
        }
        case "video":
            return (
                <Section kind="vd">
                    <div className="vd-box" {...block("image")}>
                        <Layer im={m.image} sizes="calc(100vw - 120px)" />
                        <Clip src={m.video.src} />
                    </div>
                </Section>
            )
    }
}
